import type { MemoryGroup as TicMemoryGroup } from '@timelyapp/integration-cloud-api-spec';
import type { DateTime } from 'luxon';
import type { TimelineStyleTypes } from 'redux/modules/timeline.types';
import type { EnrichedProject } from './project';
import type { Duration } from './units';
import type { Merge } from './utility';
export interface MemorySubEntities {
  id: number;
  uid: string;
  entry_id: number;
  // ^^ wtf 3 ids?
  from: string;
  to: string;
  note: string;
}

/**
 * TIC-specific properties passed via `Entry` to `Memory`
 * @todo Re-visit this approach when TIC becomes a first-class citizen.
 * @goal We should attempt to pass _raw_ TIC data through to the timeline/UI after timestamp vs ids has decision.
 */
type TicAddons = Pick<TicMemoryGroup, 'context' | 'category' | 'tool'>;

export type EntryAttributes = Array<{
  name: string;
  value: string;
}>;
// Raw from API
export interface Entry {
  id: number;
  uid: string;
  at: string | null | undefined;
  color: string;
  date: string;
  type: string;
  spam: boolean;
  importance: number;
  duration: Duration;
  entity_type: string | null | undefined;
  extra_attributes: EntryAttributes;
  from: string;
  to: string;
  icon: string;
  icon_url: string;
  icon_fallback_url: string;
  description: string;
  sub_entries: Array<MemorySubEntities>;
  title: string;
  note: string | null;
  url: string | null;
  updated_by: 'User' | 'AI';
  _tic_source?: boolean;
  tic?: TicAddons;
}

export interface SyntheticEntry extends Entry {
  projectId: number | null;
}

export interface TrainingEntry {
  id: number;
  type: string;
  uid: string;
  title: string;
  note: string;
  description: string;
  date: string;
  from: string;
  to: string;
  entry_type: string | null;
  duration: Duration;
  at: string;
  extra_attributes: EntryAttributes;
  icon: string | null;
  color: string | null;
  sub_entries: Array<MemorySubEntities>;
  icon_url: string;
  icon_fallback_url: string;
  url: string | null;
  score: number;
}

export interface MemorySubEntry {
  id: number;
  entry_id: number;
  from: string;
  to: string;
  fromTime: string;
  toTime: string;
  note: string;
  duration: Duration;
}

export interface Memory {
  id: number;
  from: string;
  to: string;
  icon: string;
  fallbackIcon: string;
  barColor: string;
  cardBgColor: string | null;
  cardTextVariant: 'light' | 'dark';
  date: string;
  fromTime: string;
  toTime: string;
  title: string;
  type: string;
  description: string;
  url: string | null | undefined;
  duration: Duration;
  isChecked: boolean | null | undefined;
  isLogged: boolean;
  isExpanded: boolean;
  /* Is this memory classified as noisy? */
  isSpam: boolean;
  importance: number;
  groupDuration: Duration;
  isPredictedToCurrentProject: boolean;
  isPredicted: boolean;
  searchString: string;
  attributes: {
    [key: string]: string;
  };
  subEntries: Array<MemorySubEntry>;
  project: {
    id: number | null | undefined;
    name: string | null | undefined;
    color: string | null | undefined;
  };
  /** Indicates this `Entry` is created from a TIC Memory */
  _tic_source?: boolean;
  /** Pass along tic-specific data. */
  tic?: TicAddons;
  cacheBuster: string;
}

export interface MemoryWithTimespan extends Memory {
  timespan: {
    parent_id: number;
    isChecked: boolean;
    color: string;
    vertical: {
      topPx: number;
      heightPx: number;
      cardOffsetTopPx: number;
      truncated: boolean;
    };
    grid: {
      cells: number;
      start: number;
      end: number;
    };
  };
}

// This becomes MemoryGroup
export interface SuggestedEntry {
  ref: string; // We made this up after XHR completed, should be server-side ideally,
  title: string;
  note: string;
  description: string;
  date: string;
  from: string | null;
  to: string | null;
  entry_ids: Array<number>;
  entries: Array<Entry>;
  importance: number;
  duration: Duration;
  icon_url: string;
  icon_fallback_url: string;
  projects: Array<{
    project_id: number;
    accuracy: number;
    count: number;
  }>;
  /** Indicates this SuggestedEntry was created from a TIC MemoryGroup */
  _tic_source?: boolean;
}

// todo: Only include what we need from projects
// See NormalizedProject for basis (just extend & keep label/value for dropdown compatibility?)
export interface SuggestedEntryProject extends EnrichedProject {
  accuracy: number;
}

export type SuggestedEntryWithProjects = Merge<
  SuggestedEntry,
  {
    projects: Array<SuggestedEntryProject>;
  }
>;

export interface MemoryGroup {
  ref: string;
  // key: string | number,
  title: string;
  subtitle: string | null | undefined;
  duration: Duration;
  durationForSelected: Duration;
  durationForUnselected: Duration;
  icon: string;
  fallbackIcon: string;
  url: string | null | undefined;
  importance: number;
  importancePercent: string;
  currentProjectPrediction: number;
  currentProjectPredictionPercent: string;
  isSpam: boolean;
  isLogged: boolean;
  isPartlyLogged: boolean;
  isChecked: boolean;
  isPartlyChecked: boolean;
  isPredicted: boolean;
  isExpanded: boolean;
  // isSpam: boolean,
  // color: string,
  memories: Array<Memory>;
  projects: Array<SuggestedEntryProject>;
  primaryProject: {
    title: string;
    color: string;
  } | null;
  // Todo: move stuff into UI like isLogged, etc.
  ui: {
    hasMemories: boolean;
    showDuration: boolean;
    showPrediction: boolean;
    showCalculatedDuration: boolean;
    showTimestamp: boolean;
  };
  status: {
    isVisible: boolean;
    message: string | null | undefined;
    variant: 'default' | 'emphasis';
  };
}

export type MemoryGroups = Array<MemoryGroup>;

export type ValidGroupStatus = 'all' | 'inbox' | 'selected' | 'other' | 'logged' | 'spam';

export interface MemoriesGroupByStatus {
  key: ValidGroupStatus;
  title: string;
  duration: Duration;
  groups: Array<MemoryGroup>;
  emptyText?: string;
  showDisplayRulesDropdown: boolean;
}

export interface MemoriesGroupedByStatus {
  all: MemoriesGroupByStatus;
  selected: MemoriesGroupByStatus;
  other: MemoriesGroupByStatus;
  totals: {
    count: number;
  };
}

/**
 * utils/timeline.js.
 */

export type MetaHourList = Array<{
  hour: number;
  label: string;
  showLabel: boolean;
}>;
export interface ExtractTimelineMetaFromEntries {
  fromDateTime: DateTime<true>;
  toDateTime: DateTime<true>;
  totalHours: number;
  totalMemories: number;
  date: string;
  isToday: boolean;
  scrollToPxFromTop: number;
  hoursList: MetaHourList;
}

export interface PredictedProjectsMap {
  [project_id: number]:
    | {
        id: number;
        entry_ids: Array<number>;
      }
    | null
    | undefined;
}

export interface EntryIdPredictionsMap {
  [entry_id: number]: {
    id: number;
    project_id: Array<number>;
    accuracy: number;
  };
}

export interface DisplayRules {
  hasRelevantMemoriesHidden: boolean;
  showLogged: boolean;
  showPredicted: boolean;
  showUnlogged: boolean;
  showSpam: boolean;
  timelineStyle: TimelineStyleTypes;
}

export enum OperatorMode {
  withHourDraft = 'withHourDraft',
  memoryInbox = 'memoryInbox',
  onSuggestion = 'onSuggestion',
  onLoggedHour = 'onLoggedHour',
  pendingDraft = 'pendingDraft',
}
